/**
 * React hooks for HTTP service consumption
 * Primary interface for making HTTP requests in React components
 */
import { useCallback } from "react";
import { useHttpContext } from "./HttpProvider";
/**
 * Hook that provides the raw HTTP service instance
 * Use this if you need direct access to the service
 */
export function useHttpService() {
    const { httpService } = useHttpContext();
    return httpService;
}
/**
 * Hook that provides convenient HTTP methods
 * Main hook for making HTTP requests
 */
export function useHttp() {
    const { httpService, getAuthToken } = useHttpContext();
    const get = useCallback((url, config) => {
        return httpService.get(url, config);
    }, [httpService]);
    const post = useCallback((url, data, config) => {
        return httpService.post(url, data, config);
    }, [httpService]);
    const put = useCallback((url, data, config) => {
        return httpService.put(url, data, config);
    }, [httpService]);
    const del = useCallback((url, config) => {
        return httpService.delete(url, config);
    }, [httpService]);
    const patch = useCallback((url, data, config) => {
        return httpService.patch(url, data, config);
    }, [httpService]);
    return {
        get,
        post,
        put,
        delete: del,
        patch,
        // Also provide access to the service instance
        service: httpService,
        // Access to auth token getter
        getAuthToken,
    };
}
/**
 * Convenience hook for CSP API calls
 * Automatically uses envConfig.cspApi as baseURL
 */
export function useCspApi() {
    const { get, post, put, delete: del, patch, service, getAuthToken } = useHttp();
    const cspGet = useCallback((url, config) => {
        return get(url, {
            ...config,
            baseURL: service.configure.toString().includes("cspApi") ? "" : config?.baseURL || "",
        });
    }, [get, service]);
    const cspPost = useCallback((url, data, config) => {
        return post(url, data, config);
    }, [post]);
    const cspPut = useCallback((url, data, config) => {
        return put(url, data, config);
    }, [put]);
    const cspDelete = useCallback((url, config) => {
        return del(url, config);
    }, [del]);
    const cspPatch = useCallback((url, data, config) => {
        return patch(url, data, config);
    }, [patch]);
    // Convenience methods for common CSP operations
    const searchAccounts = useCallback((searchTerm) => {
        return cspGet(`/api/accounts/search?q=${encodeURIComponent(searchTerm)}`);
    }, [cspGet]);
    const searchUsers = useCallback((searchTerm) => {
        return cspGet(`/api/users/search?q=${encodeURIComponent(searchTerm)}`);
    }, [cspGet]);
    const searchPrivilegeGroups = useCallback(() => {
        return cspGet(`/api/privilegeadmin/groups`);
    }, [cspGet]);
    return {
        get: cspGet,
        post: cspPost,
        put: cspPut,
        delete: cspDelete,
        patch: cspPatch,
        // Convenience methods
        searchAccounts,
        searchUsers,
        searchPrivilegeGroups,
        // Access to underlying service
        service,
        // Access to auth token getter
        getAuthToken,
    };
}
